import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileDown, Upload, Loader2 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { ExecutiveReportTemplate } from './ExecutiveReportTemplate';

export const ExecutiveReportModal = ({ 
  isOpen, 
  onClose, 
  student, 
  professorName,
  evolutionLogs,
  finances 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  student: any; 
  professorName: string;
  evolutionLogs: any[];
  finances: any[];
}) => {
  const [logoBase64, setLogoBase64] = useState<string | null>(() => {
    return localStorage.getItem('techtennis_professor_logo') || null;
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [financeMonth, setFinanceMonth] = useState('todos');
  const [financeStartDate, setFinanceStartDate] = useState('');
  const [financeEndDate, setFinanceEndDate] = useState('');
  const [sections, setSections] = useState({
    cadastro: true,
    anamnese: true,
    desempenho: true,
    financeiro: false
  });
  
  const reportRef = useRef<HTMLDivElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        setLogoBase64(result);
        localStorage.setItem('techtennis_professor_logo', result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoBase64(null);
    localStorage.removeItem('techtennis_professor_logo');
  };

  const generatePDF = async () => {
    if (!reportRef.current) return;
    setIsGenerating(true);
    try {
      const pages = reportRef.current.querySelectorAll('.pdf-page');
      if (pages.length === 0) {
        setIsGenerating(false);
        return;
      }
      
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        
        const canvas = await html2canvas(pages[i] as HTMLElement, {
          scale: 4, // Increased scale for much better image/text quality
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL('image/png'); // Changed to PNG for lossless crispness
        
        // A4 ratio preserving scaling
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = pdfWidth / imgWidth;
        const totalPdfHeight = imgHeight * ratio;
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, totalPdfHeight);
      }

      pdf.save(`Relatorio_${student.name.replace(/\s+/g, '_')}.pdf`);
      
      onClose();
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar o relatório. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,12,60,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '500px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', color: 'white', margin: 0 }}>Relatório Executivo</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={24} /></button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '12px' }}>
                  <label style={{ display: 'block', color: 'white', marginBottom: '8px', fontWeight: 600 }}>Logotipo do Professor</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {logoBase64 && <img src={logoBase64} alt="Logo" style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px', background: 'white' }} />}
                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: '8px' }}>
                      <Upload size={16} /> {logoBase64 ? 'Trocar Logo' : 'Enviar Logo'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                    </label>
                    {logoBase64 && (
                      <button type="button" onClick={handleRemoveLogo} style={{ background: 'none', border: '1px solid #EF4444', color: '#EF4444', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' }}>
                        Remover
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '12px' }}>Selecione o que incluir:</h3>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={sections.cadastro} onChange={(e) => setSections({...sections, cadastro: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                    Dados Cadastrais
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={sections.anamnese} onChange={(e) => setSections({...sections, anamnese: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                    Anamnese e Objetivos
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={sections.desempenho} onChange={(e) => setSections({...sections, desempenho: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                    Evolução e Desempenho (Radar/Linha)
                  </label>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={sections.financeiro} onChange={(e) => setSections({...sections, financeiro: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                        Extrato Financeiro
                      </label>
                      
                      {sections.financeiro && (
                        <select 
                          value={financeMonth} 
                          onChange={e => setFinanceMonth(e.target.value)}
                          style={{ padding: '6px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', outline: 'none' }}
                        >
                          <option value="todos">Todos os Meses</option>
                          <option value="custom">Período (De/Até)</option>
                          {Array.from(new Set(finances.map(f => f.date.substring(0,7)))).sort().reverse().map(m => (
                            <option key={m} value={m}>{m.split('-').reverse().join('/')}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    {sections.financeiro && financeMonth === 'custom' && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>Data Inicial</label>
                          <input type="date" value={financeStartDate} onChange={e => setFinanceStartDate(e.target.value)} style={{ width: '100%', padding: '6px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', outline: 'none', colorScheme: 'dark' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>Data Final</label>
                          <input type="date" value={financeEndDate} onChange={e => setFinanceEndDate(e.target.value)} style={{ width: '100%', padding: '6px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', outline: 'none', colorScheme: 'dark' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <button 
                    onClick={generatePDF} 
                    disabled={isGenerating}
                    style={{ width: '100%', padding: '14px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: isGenerating ? 'not-allowed' : 'pointer', opacity: isGenerating ? 0.7 : 1 }}
                  >
                    {isGenerating ? <><Loader2 size={20} className="animate-spin" /> Gerando PDF...</> : <><FileDown size={20} /> Baixar Relatório (PDF)</>}
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      {isOpen && (
        <ExecutiveReportTemplate 
          ref={reportRef} 
          professorName={professorName}
          professorLogo={logoBase64}
          student={student}
          evolutionLogs={evolutionLogs}
          allFinances={finances}
          finances={financeMonth === 'todos' 
            ? finances 
            : financeMonth === 'custom'
              ? finances.filter(f => (!financeStartDate || f.date >= financeStartDate) && (!financeEndDate || f.date <= financeEndDate))
              : finances.filter(f => f.date.startsWith(financeMonth))}
          sections={sections}
        />
      )}
    </>
  );
};
